import { useEffect, useState, useCallback } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend, BarChart } from 'recharts';
import { ChartLineUp, Gauge, Package, Warning, CheckCircle } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useDepartment } from './DepartmentContext';
import { mfgApi } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import { DateField } from '@/components/ui/date-time-field';

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const PRESETS = ['week', 'month', 'd90', 'custom'];
const PIE_COLORS = ['#EF4444', '#F59E0B', '#8B5CF6', '#3B82F6', '#EC4899', '#64748B'];

function rangeFor(preset) {
  const today = new Date();
  const to = iso(today);
  if (preset === 'week') { const d = new Date(today); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return { from: iso(d), to }; }
  if (preset === 'month') { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { from: iso(d), to }; }
  if (preset === 'd90') { const d = new Date(today); d.setDate(d.getDate() - 89); return { from: iso(d), to }; }
  return { from: '', to: '' };
}

const pct = (v) => (v == null ? '—' : `${v}%`);

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-bold tabular-nums mt-1 ${accent}`}>{value}</p>
      {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const { t } = useT();
  const { activeId } = useDepartment();
  const [preset, setPreset] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const r = preset === 'custom' ? { from: customFrom, to: customTo } : rangeFor(preset);
    const params = {};
    if (r.from) params.from = r.from;
    if (r.to) params.to = r.to;
    setLoading(true);
    mfgApi.analytics(params).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [preset, customFrom, customTo]);
  useEffect(() => { load(); }, [load, activeId]);

  const o = data?.overall;
  const byDept = data?.by_department || [];
  const trend = data?.trend || [];
  const scrapReasons = data?.scrap_by_reason || [];
  const topScrap = data?.top_scrap_items || [];
  const recent = data?.recent || [];
  const showComparison = byDept.length > 1;
  const noData = !loading && (!o || o.completed === 0);

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <ChartLineUp size={24} weight="duotone" className="text-orange-500" /> {t('mfg.nav.analytics')}
        </h1>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="analytics-range">
          {PRESETS.map((p) => (
            <button key={p} onClick={() => setPreset(p)} data-testid={`analytics-range-${p}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${preset === p ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {t(`mfg.analytics.range.${p}`)}
            </button>
          ))}
          {preset === 'custom' && (
            <span className="flex items-center gap-1.5">
              <div className="w-32"><DateField value={customFrom} onChange={(v) => setCustomFrom(v)} testId="analytics-from" inputClassName="h-8 text-xs" /></div>
              <span className="text-zinc-400 text-xs">–</span>
              <div className="w-32"><DateField value={customTo} onChange={(v) => setCustomTo(v)} testId="analytics-to" inputClassName="h-8 text-xs" /></div>
            </span>
          )}
        </div>
      </div>

      {noData ? (
        <div className="bg-white border border-zinc-200 rounded-xl py-16 text-center" data-testid="analytics-empty">
          <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">{t('mfg.analytics.empty')}</p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label={t('mfg.analytics.oee')} value={pct(o?.oee)} accent="text-indigo-600" testid="kpi-oee" />
            <Kpi label={t('mfg.analytics.availability')} value={pct(o?.availability)} accent="text-blue-600" testid="kpi-availability" />
            <Kpi label={t('mfg.analytics.performance')} value={pct(o?.performance)} accent="text-amber-600" testid="kpi-performance" />
            <Kpi label={t('mfg.analytics.quality')} value={pct(o?.quality)} accent="text-emerald-600" testid="kpi-quality" />
            <Kpi label={t('mfg.analytics.output')} value={o?.output ?? 0} accent="text-zinc-800" testid="kpi-output" />
            <Kpi label={t('mfg.analytics.scrapRate')} value={pct(o?.scrap_rate)} sub={`${t('mfg.analytics.scrap')}: ${o?.scrap ?? 0}`} accent="text-rose-600" testid="kpi-scrap" />
            <Kpi label={t('mfg.analytics.onTime')} value={pct(o?.on_time_rate)} accent="text-teal-600" testid="kpi-ontime" />
            <Kpi label={t('mfg.analytics.completed')} value={o?.completed ?? 0} accent="text-zinc-800" testid="kpi-completed" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Trend */}
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="analytics-trend">
              <h3 className="text-sm font-semibold text-zinc-700 mb-3">{t('mfg.analytics.trend')}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="output" name={t('mfg.analytics.output')} fill="#F97316" radius={[3, 3, 0, 0]} barSize={18} />
                  <Line yAxisId="r" dataKey="oee" name={t('mfg.analytics.oee')} stroke="#6366F1" strokeWidth={2} dot={false} connectNulls />
                  <Line yAxisId="r" dataKey="scrap_rate" name={t('mfg.analytics.scrapRate')} stroke="#EF4444" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Scrap by reason */}
            <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="analytics-scrap-reasons">
              <h3 className="text-sm font-semibold text-zinc-700 mb-3">{t('mfg.analytics.scrapByReason')}</h3>
              {scrapReasons.length === 0 ? (
                <div className="h-[220px] flex flex-col items-center justify-center text-center">
                  <CheckCircle size={36} weight="duotone" className="text-emerald-300 mb-2" />
                  <p className="text-xs text-zinc-400">{t('mfg.analytics.noScrap')}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={scrapReasons} dataKey="qty" nameKey="label" cx="50%" cy="50%" innerRadius={44} outerRadius={72} paddingAngle={2}>
                      {scrapReasons.map((e, i) => <Cell key={e.reason} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Cross-department comparison */}
          {showComparison && (
            <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="analytics-by-dept">
              <h3 className="text-sm font-semibold text-zinc-700 mb-3 flex items-center gap-1.5"><Gauge size={16} weight="duotone" className="text-zinc-400" /> {t('mfg.analytics.byDept')}</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={byDept} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="oee" name={t('mfg.analytics.oee')} fill="#6366F1" radius={[3, 3, 0, 0]} barSize={26} />
                  <Bar dataKey="quality" name={t('mfg.analytics.quality')} fill="#10B981" radius={[3, 3, 0, 0]} barSize={26} />
                  <Bar dataKey="scrap_rate" name={t('mfg.analytics.scrapRate')} fill="#EF4444" radius={[3, 3, 0, 0]} barSize={26} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-400 border-b border-zinc-200">
                    <th className="text-left py-1.5 font-medium">{t('mfg.analytics.dept')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.completed')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.output')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.oee')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.quality')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.scrapRate')}</th>
                    <th className="text-right font-medium">{t('mfg.analytics.onTime')}</th>
                  </tr></thead>
                  <tbody>
                    {byDept.map((d) => (
                      <tr key={d.department_id} className="border-b border-zinc-50" data-testid={`dept-row-${d.code}`}>
                        <td className="py-1.5"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: d.color }} />{d.name}</span></td>
                        <td className="text-right tabular-nums">{d.completed}</td>
                        <td className="text-right tabular-nums">{d.output}</td>
                        <td className="text-right tabular-nums font-semibold text-indigo-600">{pct(d.oee)}</td>
                        <td className="text-right tabular-nums text-emerald-600">{pct(d.quality)}</td>
                        <td className="text-right tabular-nums text-rose-600">{pct(d.scrap_rate)}</td>
                        <td className="text-right tabular-nums text-teal-600">{pct(d.on_time_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top scrap items */}
          {topScrap.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="analytics-top-scrap">
              <h3 className="text-sm font-semibold text-zinc-700 mb-3 flex items-center gap-1.5"><Warning size={16} weight="duotone" className="text-rose-400" /> {t('mfg.analytics.topScrap')}</h3>
              <div className="space-y-2">
                {topScrap.map((it) => (
                  <div key={it.item_id} className="flex items-center gap-3" data-testid={`top-scrap-${it.item_id}`}>
                    <span className="w-40 truncate text-sm text-zinc-700">{it.name}</span>
                    <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-rose-400 rounded-full" style={{ width: `${Math.min(it.scrap_rate || 0, 100)}%` }} />
                    </div>
                    <span className="w-24 text-right text-xs text-zinc-500 tabular-nums">{it.scrap} <span className="text-rose-500">({pct(it.scrap_rate)})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent completions */}
          <div>
            <h3 className="text-sm font-semibold text-zinc-700 mb-2">{t('mfg.analytics.recent')}</h3>
            <DataTable
              dataTestId="analytics-recent-table"
              rows={recent}
              rowKey={(r) => r.wo_no}
              rowTestId={(r) => `recent-${r.wo_no}`}
              empty={{ text: t('mfg.analytics.empty'), testid: 'recent-empty' }}
              columns={[
                { key: 'wo_no', header: t('mfg.wo.no'), cell: (r) => <span className="font-mono text-xs text-zinc-600">{r.wo_no}</span> },
                { key: 'product_name', primary: true, header: t('mfg.wo.product'), cell: (r) => <span className="text-zinc-800"><span data-no-translate>{r.product_name}</span></span> },
                { key: 'dept', header: '', cell: (r) => <Badge variant="outline" style={{ borderColor: r.department_color, color: r.department_color }}>{r.department_name}</Badge> },
                { key: 'good', header: t('mfg.wo.goodQty'), align: 'right', cell: (r) => <span className="tabular-nums text-emerald-600 font-medium">{r.good_qty}</span> },
                { key: 'scrap', header: t('mfg.wo.scrapQty'), align: 'right', cell: (r) => <span className="tabular-nums text-rose-600">{r.scrap_qty}</span> },
                { key: 'oee', header: t('mfg.analytics.oee'), align: 'right', cell: (r) => <span className="tabular-nums font-semibold text-indigo-600">{pct(r.oee)}</span> },
                { key: 'ontime', header: t('mfg.analytics.onTime'), cell: (r) => r.on_time == null ? <span className="text-zinc-300">—</span> : r.on_time ? <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">OK</Badge> : <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">遅延</Badge> },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
